import { useI18n } from 'next-localization';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useFetchCalendarTabDataQuery } from '@/store/fundDetails/services/fundDetailsApi';
import useHelpers from 'hooks/helpers';
import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import FundDetailsStripedTable from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsStripedTable';
import { useMemo } from 'react';
import { standardFormat } from '@/constants/dateFormats';
import useWindowDimensions from 'hooks/useWindowDimensions';
import styles from '../../../styles/FundDetailsCalendarTab.module.scss';

const quarterToDateTimePeriodID = '83189b89-48d1-4278-ae6e-53b5d963ff16';

type PerformanceSet = {
  performanceCategoryID: string;
  performanceCategoryType: string;
  performanceLabel: string;
  values: {
    performanceTimePeriod: string;
    performanceTimePeriodID: string;
    value: string;
  }[];
};

const FundDetailsCalendarTab = () => {
  const { t } = useI18n();
  const { getErrorDictionary, formatNumber, localizeDate } = useHelpers();
  const { deviceType } = useWindowDimensions();
  const { language, shareClass, countryCode, correlationFundRangeId, site } = usePayload();
  const { data, isLoading, error } = useFetchCalendarTabDataQuery({
    language,
    shareClass,
    countryCode,
    correlationFundRangeId,
    site,
  });

  const performanceSet: Array<PerformanceSet> = useMemo(() => {
    const set: Array<PerformanceSet> =
      data?.content.performanceSet.map((item) => ({
        ...item,
        values: [...item.values], // shallow clone of values array
      })) ?? [];
    if (
      set.some((item) =>
        item.values.some((value) => value.performanceTimePeriodID === quarterToDateTimePeriodID)
      )
    ) {
      set.forEach((item) => {
        item.values.sort((a, b) => a.performanceTimePeriod.localeCompare(b.performanceTimePeriod));
      });
    } else {
      set.forEach((item) => {
        item.values.sort((a, b) => b.performanceTimePeriod.localeCompare(a.performanceTimePeriod));
      });
    }
    return set || [];
  }, [data]);

  const tableColumnsName = useMemo(() => {
    const names = [];
    for (const item of performanceSet) {
      for (const value of item.values) {
        names.push(value.performanceTimePeriod);
      }
    }
    return new Set(names);
  }, [performanceSet]);

  const mappedData = useMemo(() => {
    const mapped = [];

    for (const item of performanceSet) {
      mapped.push({
        rowLabel: item.performanceLabel,
        values: item.values?.map(({ value }) => formatNumber(value)),
      });
    }

    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceSet]);

  const tableData = useMemo(() => {
    if (!mappedData)
      return {
        tableHeaderLabels: '',
        tableBodyRowLabels: [],
      };

    return {
      tableHeaderLabels: tableColumnsName,
      tableBodyRowLabels: mappedData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedData, tableColumnsName]);

  const footnote = useMemo(() => {
    const lastRelevantDate = data?.content?.lastRelevantDate;
    if (!lastRelevantDate) return '';
    return `${t('fundDetailsPerformanceCumulativeFootnote')} ${localizeDate(
      lastRelevantDate,
      standardFormat
    )}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isScrollable = useMemo(() => {
    if (data?.content === null) return false;
    if (deviceType === 'mobile') {
      return tableColumnsName.size >= 2;
    } else if (deviceType === 'tablet') {
      return tableColumnsName.size > 4;
    } else {
      return tableColumnsName.size > 6;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, data, tableColumnsName]);

  return (
    <FundDetailsTabRow fullWidth className="mt-4">
      {isLoading ? (
        <FundDetailsTabSkeleton />
      ) : error ? (
        <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
      ) : tableData && tableData.tableBodyRowLabels.length ? (
        <FundDetailsStripedTable
          isScrollable={isScrollable}
          data={tableData}
          footnote={footnote}
          tableId="calendarTable"
          wrapperId="calendarWrapper"
          className={styles['calendar-table']}
        />
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}
    </FundDetailsTabRow>
  );
};

export default FundDetailsCalendarTab;
